import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Table, Alert, Spinner, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faSave, faTrashAlt, faFileUpload
} from '@fortawesome/free-solid-svg-icons';
import { monthlySalaryService, exchangeRateService, recordService } from '../../services/api';
import { MonthlySalary, Employee } from '../../types';
import type { MonthlySalaryDetail } from '../../types';
import '../../styles/MonthlySalary.css';

interface EditableRow extends MonthlySalaryDetail {
  isNew?: boolean;
  isEditing?: boolean;
}

const MonthlySalaryDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isCreateMode = id === 'create';

  const [loading, setLoading] = useState<boolean>(!isCreateMode);
  const [saving, setSaving] = useState<boolean>(false);
  const [salaryMonth, setSalaryMonth] = useState<string>('');
  const [exchangeRate, setExchangeRate] = useState<string>('');
  const [totalInsurance, setTotalInsurance] = useState<string>('0');
  const [details, setDetails] = useState<EditableRow[]>([]);
  const [availableEmployees, setAvailableEmployees] = useState<Employee[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<EditableRow | null>(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState<boolean>(false);
  const [newEmployeeNo, setNewEmployeeNo] = useState<string>('');
  const [newEmployeeName, setNewEmployeeName] = useState<string>('');
  const [pendingEmployeeName, setPendingEmployeeName] = useState<string>('');
  const [addingRecord, setAddingRecord] = useState<boolean>(false);
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState<boolean>(false);

  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
    show: boolean;
  }>({
    message: '',
    type: 'success',
    show: false
  });

  useEffect(() => {
    if (!isCreateMode && id) {
      fetchRecord(parseInt(id));
    } else {
      // Set default salary month to last month
      const today = new Date();
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const defaultMonth = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
      setSalaryMonth(defaultMonth);
      
      // Fetch latest exchange rate for create mode
      fetchLatestExchangeRate();
      // Note: Don't add empty row here - it will be added by fetchAllActiveEmployees
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isCreateMode]);

  useEffect(() => {
    if (!isCreateMode && id) {
      fetchAvailableEmployees(parseInt(id));
    } else if (isCreateMode) {
      // In create mode, fetch all active employees
      fetchAllActiveEmployees();
    }
  }, [id, isCreateMode, details.length]);

  const fetchRecord = async (recordId: number) => {
    try {
      setLoading(true);
      const response = await monthlySalaryService.getById(recordId);
      const record: MonthlySalary = response.data.data;

      setSalaryMonth(record.salary_month);
      setExchangeRate(record.exch_rate.toString());
      setTotalInsurance(record.total_insurance.toString());
      setDetails(record.details || []);
    } catch (error) {
      console.error('Error fetching monthly salary record:', error);
      showNotification('Error loading monthly salary record', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableEmployees = async (recordId: number) => {
    try {
      const response = await monthlySalaryService.getAvailableEmployees(recordId);
      setAvailableEmployees(response.data.data || []);
    } catch (error) {
      console.error('Error fetching available employees:', error);
    }
  };

  const fetchAllActiveEmployees = async () => {
    try {
      const response = await monthlySalaryService.getAvailableEmployees(0); // Pass 0 to get all employees
      const employees = response.data.data || [];
      setAvailableEmployees(employees);
      
      // In create mode, pre-populate all employees in the detail list
      if (isCreateMode && employees.length > 0) {
        const employeeDetails = employees.map((emp: Employee) => ({
          salary_month_id: 0,
          employee_id: emp.id,
          employee_no: emp.employee_no,
          employee_name: emp.full_name,
          ot_days: 0,
          wrk_day_rate: 0,
          ot_amount: 0,
          base_salary: 0,
          note: '',
          isNew: true
        }));
        setDetails(employeeDetails);
      }
    } catch (error) {
      console.error('Error fetching all active employees:', error);
    }
  };

  const fetchLatestExchangeRate = async () => {
    try {
      const response = await exchangeRateService.getCurrent();
      console.log('Exchange rate response:', response.data);
      if (response.data) {
        // The API returns the exchange rate object directly
        const rate = response.data.rate || response.data.vnd_to_usd;
        if (rate) {
          setExchangeRate(rate.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setNotification({ message, type, show: true });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 4000);
  };

  const handleBack = () => {
    navigate('/monthly-salary');
  };

  const calculateWrkDayRate = (baseSalary: number): number => {
    return Math.round(baseSalary / 22);
  };

  const calculateSalaryVND = (baseSalary: number | string, otAmount: number | string): number => {
    const base = typeof baseSalary === 'string' ? parseFloat(baseSalary.replace(/,/g, '')) || 0 : baseSalary;
    const ot = typeof otAmount === 'string' ? parseFloat(otAmount.replace(/,/g, '')) || 0 : otAmount;
    return base + ot;
  };

  const calculateSalaryUSD = (salaryVND: number, exchRate: number): number => {
    console.log('calculateSalaryUSD called with:', { salaryVND, exchRate, type_vnd: typeof salaryVND, type_rate: typeof exchRate });
    if (exchRate === 0) return 0;
    const result = parseFloat((salaryVND / exchRate).toFixed(1));
    console.log('Result:', result);
    return result;
  };

  const calculateInsuranceUSD = (): number => {
    const insuranceVND = parseFloat(String(totalInsurance).replace(/,/g, '')) || 0;
    const exchRate = parseFloat(String(exchangeRate).replace(/,/g, '')) || 0;
    if (exchRate === 0) return 0;
    return parseFloat((insuranceVND / exchRate).toFixed(1));
  };

  const handleDetailChange = (index: number, field: keyof EditableRow, value: any) => {
    const newDetails = [...details];
    const detail = newDetails[index];

    if (field === 'employee_id') {
      // Employee selected from dropdown
      const selectedEmployeeId = parseInt(value);
      
      // Check for duplicate employee
      const isDuplicate = newDetails.some((d, i) => 
        i !== index && d.employee_id === selectedEmployeeId && selectedEmployeeId > 0
      );
      
      if (isDuplicate) {
        showNotification('This employee has already been added to this salary record', 'error');
        return;
      }
      
      const employee = availableEmployees.find(e => e.id === selectedEmployeeId);
      if (employee) {
        detail.employee_id = employee.id;
        detail.employee_no = employee.employee_no;
        detail.employee_name = employee.full_name;
        detail.isNew = true;
      }
    } else if (field === 'employee_name') {
      // User is typing in employee name field
      detail.employee_name = value;
    } else if (field === 'ot_days') {
      const numValue = parseFloat(value.replace(/,/g, '')) || 0;
      detail.ot_days = numValue;
    } else if (field === 'wrk_day_rate') {
      const numValue = parseFloat(value.replace(/,/g, '')) || 0;
      detail.wrk_day_rate = numValue;
    } else if (field === 'ot_amount') {
      const numValue = parseFloat(value.replace(/,/g, '')) || 0;
      detail.ot_amount = numValue;
    } else if (field === 'base_salary') {
      const numValue = parseFloat(value.replace(/,/g, '')) || 0;
      detail.base_salary = numValue;
      // Auto-calculate work day rate when base salary changes
      detail.wrk_day_rate = calculateWrkDayRate(numValue);
    } else if (field === 'note') {
      detail.note = value;
    } else {
      (detail as any)[field] = value;
    }

    // Check if this is the last row and has data - add new empty row
    if (index === newDetails.length - 1 && detail.employee_id) {
      addNewRow();
    }

    setDetails(newDetails);
  };

  const handleCreateEmployee = async () => {
    if (!newEmployeeNo || !newEmployeeName) {
      showNotification('Employee number and name are required', 'error');
      return;
    }

    try {
      const response = await monthlySalaryService.createEmployee({
        employee_no: newEmployeeNo,
        full_name: newEmployeeName
      });

      showNotification('Employee created successfully', 'success');
      
      // Add the new employee to available employees
      const newEmployee = response.data.data;
      setAvailableEmployees(prev => [...prev, newEmployee]);
      
      // Find the row that triggered this and set the employee
      const rowIndex = details.findIndex(d => d.employee_name === pendingEmployeeName);
      if (rowIndex >= 0) {
        handleDetailChange(rowIndex, 'employee_id', newEmployee.id);
      }
      
      // Reset and close modal
      setNewEmployeeNo('');
      setNewEmployeeName('');
      setPendingEmployeeName('');
      setShowEmployeeModal(false);
    } catch (error: any) {
      console.error('Error creating employee:', error);
      const errorMessage = error.response?.data?.message || 'Error creating employee';
      showNotification(errorMessage, 'error');
    }
  };

  const addNewRow = () => {
    setDetails(prev => [
      ...prev,
      {
        salary_month_id: parseInt(id || '0'),
        employee_id: 0,
        ot_days: 0,
        wrk_day_rate: 0,
        ot_amount: 0,
        base_salary: 0,
        note: '',
        isNew: true
      }
    ]);
  };

  const handleDeleteClick = (detail: EditableRow) => {
    setDeleteTarget(detail);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    // If it's a new row, just remove it from the list
    if (deleteTarget.isNew && !deleteTarget.id) {
      setDetails(prev => prev.filter(d => d !== deleteTarget));
      setShowDeleteModal(false);
      setDeleteTarget(null);
      return;
    }

    // Otherwise, delete from backend
    try {
      if (deleteTarget.id) {
        await monthlySalaryService.deleteDetail(deleteTarget.id);
        showNotification('Employee removed successfully', 'success');
        setDetails(prev => prev.filter(d => d.id !== deleteTarget.id));
      }
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Error deleting detail:', error);
      showNotification('Error removing employee', 'error');
    }
  };

  const handleSave = async () => {
    // Validation
    if (isCreateMode && !salaryMonth) {
      showNotification('Please select a month', 'error');
      return;
    }

    if (!exchangeRate || parseFloat(exchangeRate) <= 0) {
      showNotification('Please enter a valid exchange rate', 'error');
      return;
    }

    // Filter out empty rows
    const validDetails = details.filter(d => d.employee_id > 0);

    try {
      setSaving(true);

      if (isCreateMode) {
        // Create new monthly salary record
        const year = parseInt(salaryMonth.substring(0, 4));
        const month = parseInt(salaryMonth.substring(5, 7));
        const salaryMonthStr = `${year}${month.toString().padStart(2, '0')}`;

        const createData = {
          salary_month: salaryMonthStr,
          year,
          month,
          total_insurance: parseFloat(totalInsurance) || 0,
          exch_rate: parseFloat(exchangeRate)
        };

        const response = await monthlySalaryService.create(createData);
        const newRecord = response.data.data;

        // Batch update details
        if (validDetails.length > 0) {
          await monthlySalaryService.batchUpdateDetails(newRecord.id, validDetails);
        }

        showNotification('Monthly salary record created successfully', 'success');
        navigate(`/monthly-salary/${newRecord.id}`);
      } else {
        // Update existing record
        const updateData = {
          total_insurance: parseFloat(totalInsurance) || 0,
          exch_rate: parseFloat(exchangeRate)
        };

        await monthlySalaryService.update(parseInt(id!), updateData);

        // Batch update details
        if (validDetails.length > 0) {
          await monthlySalaryService.batchUpdateDetails(parseInt(id!), validDetails);
        }

        showNotification('Monthly salary record updated successfully', 'success');
        fetchRecord(parseInt(id!));
      }
    } catch (error: any) {
      console.error('Error saving monthly salary:', error);
      const errorMessage = error.response?.data?.message || 'Error saving monthly salary record';
      showNotification(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const getTotalVND = (): number => {
    return details.reduce((sum, d) => {
      if (d.employee_id > 0) {
        return sum + calculateSalaryVND(d.base_salary, d.ot_amount);
      }
      return sum;
    }, 0);
  };

  const getTotalUSD = (): number => {
    const totalVND = getTotalVND();
    const exchRate = parseFloat(String(exchangeRate).replace(/,/g, '')) || 0;
    return calculateSalaryUSD(totalVND, exchRate);
  };

  const getGrandTotalVND = (): number => {
    const salaryTotal = getTotalVND();
    const insurance = parseFloat(String(totalInsurance).replace(/,/g, '')) || 0;
    return salaryTotal + insurance;
  };

  const getGrandTotalUSD = (): number => {
    const grandTotalVND = getGrandTotalVND();
    const exchRate = parseFloat(String(exchangeRate).replace(/,/g, '')) || 0;
    return calculateSalaryUSD(grandTotalVND, exchRate);
  };

  const getEmployeeCount = (): number => {
    return details.filter(d => d.employee_id > 0).length;
  };

  const handleAddSalaryRecord = async () => {
    try {
      setAddingRecord(true);
      
      const totalSalaryVND = getTotalVND();
      const totalSalaryUSD = getTotalUSD();
      
      if (totalSalaryVND === 0) {
        showNotification('Total salary is zero. Cannot create record.', 'error');
        return;
      }
      
      // Format month for description
      const monthStr = salaryMonth.length === 6 
        ? `${salaryMonth.substring(0, 4)}-${salaryMonth.substring(4, 6)}`
        : salaryMonth;
      
      const recordData = {
        record_date: `${monthStr}-01`, // First day of the month
        description: `Salary of ${monthStr}`,
        category_id: 5, // Salary category
        amount_vnd: -Math.abs(totalSalaryVND), // Negative for expense
        amount_usd: -Math.abs(totalSalaryUSD), // Negative for expense
        tax_percent: 0,
        tax_amount_vnd: 0,
        tax_amount_usd: 0,
        record_type: 'expense',
        account_type: 'company',
        skipDuplicateCheck: false
      };
      
      const response = await recordService.create(recordData);
      
      // Check if duplicate was detected
      if (response.data?.isDuplicate) {
        showNotification(
          `A salary record for ${monthStr} already exists. Please check the Records page.`,
          'warning'
        );
        return;
      }
      
      showNotification('Salary record added successfully', 'success');
    } catch (error: any) {
      console.error('Error adding salary record:', error);
      
      // Check if it's a duplicate error
      if (error.response?.status === 409 || error.response?.data?.isDuplicate) {
        const monthStr = salaryMonth.length === 6 
          ? `${salaryMonth.substring(0, 4)}-${salaryMonth.substring(4, 6)}`
          : salaryMonth;
        showNotification(
          `A salary record for ${monthStr} already exists. Please check the Records page.`,
          'warning'
        );
      } else {
        const errorMessage = error.response?.data?.message || 'Error adding salary record';
        showNotification(errorMessage, 'error');
      }
    } finally {
      setAddingRecord(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      showNotification('Please select a file to import', 'error');
      return;
    }

    try {
      setImporting(true);
      const recordId = isCreateMode ? 0 : parseInt(id!);
      
      if (isCreateMode) {
        showNotification('Please save the monthly salary record first before importing', 'error');
        return;
      }

      const response = await monthlySalaryService.importFromExcel(recordId, selectedFile);
      
      if (response.data.success) {
        const result = response.data.data;
        showNotification(
          `Successfully imported ${result.imported} employees (${result.created} new, ${result.updated} updated)`,
          'success'
        );
        
        // Refresh the data
        fetchRecord(recordId);
        
        // Close modal and reset
        setShowImportModal(false);
        setSelectedFile(null);
      }
    } catch (error: any) {
      console.error('Error importing Excel:', error);
      const errorMessage = error.response?.data?.message || 'Error importing Excel file';
      showNotification(errorMessage, 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleAddInsuranceRecord = async () => {
    try {
      setAddingRecord(true);
      
      const insuranceVND = parseFloat(String(totalInsurance).replace(/,/g, '')) || 0;
      const insuranceUSD = calculateInsuranceUSD();
      
      if (insuranceVND === 0) {
        showNotification('Insurance amount is zero. Cannot create record.', 'error');
        return;
      }
      
      // Format month for description
      const monthStr = salaryMonth.length === 6 
        ? `${salaryMonth.substring(0, 4)}-${salaryMonth.substring(4, 6)}`
        : salaryMonth;
      
      const recordData = {
        record_date: `${monthStr}-01`, // First day of the month
        description: `Insurance of ${monthStr}`,
        category_id: 5, // Salary category
        amount_vnd: -Math.abs(insuranceVND), // Negative for expense
        amount_usd: -Math.abs(insuranceUSD), // Negative for expense
        tax_percent: 0,
        tax_amount_vnd: 0,
        tax_amount_usd: 0,
        record_type: 'expense',
        account_type: 'company',
        skipDuplicateCheck: false
      };
      
      const response = await recordService.create(recordData);
      
      // Check if duplicate was detected
      if (response.data?.isDuplicate) {
        showNotification(
          `An insurance record for ${monthStr} already exists. Please check the Records page.`,
          'warning'
        );
        return;
      }
      
      showNotification('Insurance record added successfully', 'success');
    } catch (error: any) {
      console.error('Error adding insurance record:', error);
      
      // Check if it's a duplicate error
      if (error.response?.status === 409 || error.response?.data?.isDuplicate) {
        const monthStr = salaryMonth.length === 6 
          ? `${salaryMonth.substring(0, 4)}-${salaryMonth.substring(4, 6)}`
          : salaryMonth;
        showNotification(
          `An insurance record for ${monthStr} already exists. Please check the Records page.`,
          'warning'
        );
      } else {
        const errorMessage = error.response?.data?.message || 'Error adding insurance record';
        showNotification(errorMessage, 'error');
      }
    } finally {
      setAddingRecord(false);
    }
  };

  const formatNumber = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    if (isNaN(num)) return '0';
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatDecimal = (value: number) => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    });
  };

  return (
    <div className="monthly-salary-detail-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <Button variant="outline-secondary" onClick={handleBack}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </Button>
          <h2>{isCreateMode ? 'Create Monthly Salary' : 'Edit Monthly Salary'}</h2>
        </div>
        <div className="header-right">
          {!isCreateMode && (
            <>
              <Button 
                variant="success" 
                onClick={() => setShowImportModal(true)}
                className="me-2"
              >
                <FontAwesomeIcon icon={faFileUpload} /> Import Excel
              </Button>
              <Button 
                variant="success" 
                onClick={handleAddSalaryRecord}
                disabled={addingRecord || getTotalVND() === 0}
                className="me-2"
              >
                {addingRecord ? 'Adding...' : 'Add Salary to Records'}
              </Button>
              <Button 
                variant="info" 
                onClick={handleAddInsuranceRecord}
                disabled={addingRecord || parseFloat(totalInsurance) === 0}
                className="me-2"
              >
                {addingRecord ? 'Adding...' : 'Add Insurance to Records'}
              </Button>
            </>
          )}
          <Button 
            variant="primary" 
            onClick={handleSave}
            disabled={saving}
          >
            <FontAwesomeIcon icon={faSave} /> {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Notification */}
      {notification.show && (
        <Alert 
          variant={notification.type === 'error' ? 'danger' : notification.type} 
          dismissible 
          onClose={() => setNotification(prev => ({ ...prev, show: false }))}
          className="notification-alert"
        >
          {notification.message}
        </Alert>
      )}

      {/* Month selection (create mode only) */}
      {isCreateMode && (
        <div className="month-selection">
          <Form.Group>
            <Form.Label>Salary Month</Form.Label>
            <Form.Control
              type="month"
              value={salaryMonth}
              onChange={(e) => setSalaryMonth(e.target.value)}
              required
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Total Insurance (VND)</Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                type="text"
                value={formatNumber(totalInsurance)}
                onChange={(e) => {
                  const value = e.target.value.replace(/,/g, '');
                  setTotalInsurance(value);
                }}
                placeholder="0"
                className="text-end"
                style={{ flex: 1 }}
              />
              <span className="text-muted" style={{ minWidth: '120px', fontSize: '14px' }}>
                ≈ ${formatDecimal(calculateInsuranceUSD())} USD
              </span>
            </div>
          </Form.Group>
        </div>
      )}

      {/* Edit mode month display */}
      {!isCreateMode && !loading && (
        <div className="month-display">
          <div className="info-item">
            <strong>Month:</strong> {salaryMonth.substring(0, 4)}-{salaryMonth.substring(4, 6)}
          </div>
          <div className="info-item" style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
            <div>
              <strong>Total Insurance (VND):</strong>
              <div className="d-inline-flex align-items-center gap-2" style={{ marginLeft: '10px' }}>
                <Form.Control
                  type="text"
                  value={formatNumber(totalInsurance)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/,/g, '');
                    setTotalInsurance(value);
                  }}
                  placeholder="0"
                  className="text-end"
                  style={{ width: '180px' }}
                />
                <span className="text-muted" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                  ≈ ${formatDecimal(calculateInsuranceUSD())} USD
                </span>
              </div>
            </div>
            <div>
              <strong>Exchange Rate (VND/USD):</strong>
              <div className="d-inline-flex align-items-center" style={{ marginLeft: '10px' }}>
                <Form.Control
                  type="text"
                  value={formatNumber(exchangeRate)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/,/g, '');
                    setExchangeRate(value);
                  }}
                  placeholder="Enter exchange rate"
                  className="text-end"
                  style={{ width: '180px' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading spinner */}
      {loading && (
        <div className="text-center my-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}

      {/* Details table */}
      {!loading && (
        <div className="details-table-container">
          <Table striped bordered hover responsive className="details-table">
            <thead>
              <tr>
                <th style={{ width: '100px' }}>Emp. No</th>
                <th style={{ width: '200px' }}>Employee Name</th>
                <th style={{ width: '100px' }}>OT Days</th>
                <th style={{ width: '120px' }}>Wrk Day Rate</th>
                <th style={{ width: '120px' }}>OT Amount</th>
                <th style={{ width: '150px' }}>Base Salary</th>
                <th style={{ width: '150px' }}>Salary (VND)</th>
                <th style={{ width: '150px' }}>Salary (USD)</th>
                <th style={{ width: '200px' }}>Note</th>
                <th style={{ width: '80px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {details.map((detail, index) => {
                const salaryVND = calculateSalaryVND(detail.base_salary, detail.ot_amount);
                const exchRateStr = String(exchangeRate).replace(/,/g, '');
                const exchRate = parseFloat(exchRateStr) || 0;
                const salaryUSD = exchRate > 0 ? calculateSalaryUSD(salaryVND, exchRate) : 0;
                console.log('Row', index, '- ExchRate:', exchangeRate, '→', exchRateStr, '→', exchRate, 'VND:', salaryVND, 'USD:', salaryUSD);
                
                return (
                  <tr key={detail.id || `new-${index}`}>
                    <td>{detail.employee_no || '-'}</td>
                    <td>
                      {detail.employee_id > 0 ? (
                        detail.employee_name
                      ) : (
                        <Form.Control
                          as="select"
                          value={detail.employee_id || ''}
                          onChange={(e) => handleDetailChange(index, 'employee_id', e.target.value)}
                          size="sm"
                        >
                          <option value="">Select employee...</option>
                          {availableEmployees.map(emp => (
                            <option key={emp.id} value={emp.id}>
                              {emp.employee_no} - {emp.full_name}
                            </option>
                          ))}
                        </Form.Control>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={detail.ot_days}
                        onChange={(e) => handleDetailChange(index, 'ot_days', e.target.value)}
                        size="sm"
                        disabled={!detail.employee_id}
                        className="text-end"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={formatNumber(detail.wrk_day_rate)}
                        onChange={(e) => handleDetailChange(index, 'wrk_day_rate', e.target.value)}
                        size="sm"
                        disabled={!detail.employee_id}
                        className="text-end"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={formatNumber(detail.ot_amount)}
                        onChange={(e) => handleDetailChange(index, 'ot_amount', e.target.value)}
                        size="sm"
                        disabled={!detail.employee_id}
                        className="text-end"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={formatNumber(detail.base_salary)}
                        onChange={(e) => handleDetailChange(index, 'base_salary', e.target.value)}
                        size="sm"
                        disabled={!detail.employee_id}
                        className="text-end"
                      />
                    </td>
                    <td className="text-end">{formatNumber(salaryVND)}</td>
                    <td className="text-end">{formatDecimal(salaryUSD)}</td>
                    <td>
                      <Form.Control
                        type="text"
                        value={detail.note || ''}
                        onChange={(e) => handleDetailChange(index, 'note', e.target.value)}
                        size="sm"
                        disabled={!detail.employee_id}
                      />
                    </td>
                    <td>
                      {detail.employee_id > 0 && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteClick(detail)}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan={6} className="text-end"><strong>SALARY TOTAL:</strong></td>
                <td className="text-end"><strong>{formatNumber(getTotalVND())}</strong></td>
                <td className="text-end"><strong>{formatDecimal(getTotalUSD())}</strong></td>
                <td colSpan={2} className="text-center">
                  <strong>{getEmployeeCount()} employees</strong>
                </td>
              </tr>
              <tr className="total-row">
                <td colSpan={6} className="text-end"><strong>INSURANCE:</strong></td>
                <td className="text-end"><strong>{formatNumber(totalInsurance)}</strong></td>
                <td className="text-end"><strong>{formatDecimal(calculateInsuranceUSD())}</strong></td>
                <td colSpan={2}></td>
              </tr>
              <tr style={{ background: '#10b981', color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                <td colSpan={6} className="text-end" style={{ padding: '15px' }}><strong>GRAND TOTAL (Salary + Insurance):</strong></td>
                <td className="text-end" style={{ padding: '15px' }}><strong>{formatNumber(getGrandTotalVND())}</strong></td>
                <td className="text-end" style={{ padding: '15px' }}><strong>{formatDecimal(getGrandTotalUSD())}</strong></td>
                <td colSpan={2}></td>
              </tr>
              <tr className="action-row">
                <td colSpan={10} className="text-center" style={{ padding: '1rem' }}>
                  <div className="d-flex justify-content-center gap-3">
                    <Button 
                      variant="success" 
                      onClick={handleAddSalaryRecord}
                      disabled={addingRecord || getTotalVND() === 0}
                    >
                      {addingRecord ? 'Adding...' : 'Add Salary to Records'}
                    </Button>
                    <Button 
                      variant="info" 
                      onClick={handleAddInsuranceRecord}
                      disabled={addingRecord || parseFloat(totalInsurance) === 0}
                    >
                      {addingRecord ? 'Adding...' : 'Add Insurance to Records'}
                    </Button>
                  </div>
                </td>
              </tr>
            </tfoot>
          </Table>
        </div>
      )}

      {/* Delete confirmation modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="d-flex align-items-center">
            <FontAwesomeIcon icon={faTrashAlt} className="text-danger me-2" />
            Remove Employee
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <p className="mb-2">Are you sure you want to remove this employee from the salary record?</p>
          <div className="alert alert-light border mb-0">
            <strong>Employee:</strong> {deleteTarget?.employee_name}<br />
            <strong>Employee No:</strong> {deleteTarget?.employee_no || 'N/A'}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            <FontAwesomeIcon icon={faTrashAlt} className="me-1" />
            Remove
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create employee modal */}
      <Modal show={showEmployeeModal} onHide={() => setShowEmployeeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Employee <strong>{pendingEmployeeName}</strong> was not found. Create a new employee?</p>
          <Form.Group className="mb-3">
            <Form.Label>Employee No *</Form.Label>
            <Form.Control
              type="text"
              value={newEmployeeNo}
              onChange={(e) => setNewEmployeeNo(e.target.value)}
              placeholder="Enter employee number"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Full Name *</Form.Label>
            <Form.Control
              type="text"
              value={newEmployeeName}
              onChange={(e) => setNewEmployeeName(e.target.value)}
              placeholder="Enter full name"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEmployeeModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCreateEmployee}>
            Create Employee
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Import Excel modal */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="d-flex align-items-center">
            <FontAwesomeIcon icon={faFileUpload} className="text-success me-2" />
            Import Salary Details from Excel
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <div className="alert alert-info mb-3">
            <strong>Excel Format:</strong>
            <ul className="mb-0 mt-2">
              <li>Column A: Emp. No.</li>
              <li>Column B: Employee Name</li>
              <li>Column C: OT days</li>
              <li>Column D: Wrk Day Rate</li>
              <li>Column E: OT Amount (VND)</li>
              <li>Column F: Base Salary (VND)</li>
              <li>Column G: Salary VND (calculated)</li>
              <li>Column H: Salary USD (calculated)</li>
              <li>Column I: Note</li>
            </ul>
            <p className="mt-2 mb-0"><small>First row should be headers and will be skipped.</small></p>
          </div>
          <Form.Group>
            <Form.Label>Select Excel File</Form.Label>
            <Form.Control
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
            />
          </Form.Group>
          {selectedFile && (
            <div className="mt-2">
              <small className="text-muted">Selected: {selectedFile.name}</small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="outline-secondary" onClick={() => {
            setShowImportModal(false);
            setSelectedFile(null);
          }}>
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleImport}
            disabled={!selectedFile || importing}
          >
            <FontAwesomeIcon icon={faFileUpload} className="me-1" />
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MonthlySalaryDetailPage;
